import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';
import { produce } from 'immer';

import * as MyOrganizationActions from './my-organization.actions';
import { MyOrganizationEntity } from './my-organization.models';
import { cloneDeep } from 'lodash';

export const MY_ORGANIZATION_FEATURE_KEY = 'myOrganization';

function indexRemapping(type: any) {
  return type.map((item: any, i: any) => {
    return {
      ...item,
      index: i + 1,
    };
  });
}

export interface MyOrganizationState extends EntityState<MyOrganizationEntity> {
  selectedId?: string | number; // which MyOrganization record has been selected
  loaded: boolean; // has the MyOrganization list been loaded
  error?: string | null; // last known error (if any)
  manageTeamsData: [] | any;
  deleteManageData: [] | any;
  usersList: [] | any;
  projectWorkflowList: [] | any;
  manageScenarioWorkflow: any;
  productGroupsList: [] | any;
  suppliersList: [] | any;
  litterImpactData: [] | any;
  litterImpactFormData: [] | any;
  yearEndsOnData: [] | any;
  yearEndsReplica: [] | any;
  customCategoryData: [] | any;
  customSpeedosData: [] | any;
  packagingMetricsData: [] | any;
  manageUserDetailsData: [] | any;
  manageTeamMembershipData: [] | any;
  adminOrganizationMembershipData: [] | any;
  manageTeamData: [] | any;
  projectSavingLogsData: [] | any;
  ssoGroupList: [] | any;
  myOrganizationManagageUserData: [] | any;
  paymentTransactionList: [] | any;
  invoiceData: [] | any;
  invoiceDownloadData: [] | any;
  weightingList: [] | any;
  skuYearProcess: [] | any;
  skuYearProcessError?: string | null;
  orgWeightingData: [] | any;
  checkUpdateActionSKU: boolean;
}

export interface MyOrganizationPartialState {
  readonly [MY_ORGANIZATION_FEATURE_KEY]: MyOrganizationState;
}

function validationMessage(filteredData: any, typeName: any, typeId: any) {
  const processRequired = filteredData.filter((data: any) => data[typeName] == 'Select a year')
    .length
    ? true
    : false;
  const unitsRequired = filteredData.filter((data: any) => !data?.numberOfUnitsProduced).length
    ? true
    : false;
  const error = processRequired
    ? 'Please fill all the mandatory information to proceed further in the process. '
    : unitsRequired
    ? 'Please enter number of units'
    : '';
  return error;
}

export const myOrganizationAdapter: EntityAdapter<MyOrganizationEntity> =
  createEntityAdapter<MyOrganizationEntity>();

export const initialMyOrganizationState: MyOrganizationState =
  myOrganizationAdapter.getInitialState({
    // set initial required properties
    loaded: false,
    usersList: [],
    manageTeamsData: [],
    deleteManageData: [],
    projectWorkflowList: [],
    productGroupsList: [],
    suppliersList: [],
    litterImpactData: [],
    litterImpactFormData: [],
    yearEndsOnData: [],
    customCategoryData: [],
    manageScenarioWorkflow: [],
    customSpeedosData: [],
    packagingMetricsData: [],
    projectSavingLogsData: [],
    invoiceDownloadData: [],
    ssoGroupList: [],
    myOrganizationManagageUserData: [],
    manageUserDetailsData: [],
    manageTeamMembershipData: [],
    adminOrganizationMembershipData: [],
    manageTeamData: [],
    paymentTransactionList: [],
    invoiceData: [],
    yearEndsReplica: [],
    weightingList: [],
    skuYearProcess: [],
    orgWeightingData: [],
    checkUpdateActionSKU: false,
  });

const reducer = createReducer(
  initialMyOrganizationState,
  on(MyOrganizationActions.getUsersListSuccess, (state, { usersList }) => ({
    ...state,
    usersList: usersList,
  })),
  //payment transaction
  on(MyOrganizationActions.getPaymentTransaction, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(MyOrganizationActions.getPaymentTransactionSuccess, (state, { paymentTransactionList }) => {
    return { ...state, paymentTransactionList: paymentTransactionList, loaded: true };
  }),
  on(MyOrganizationActions.getPaymentTransactionFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(MyOrganizationActions.getManageTeamsList, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(MyOrganizationActions.getManageTeamsListSuccess, (state, { teamsPayload }) => {
    return { ...state, manageTeamsData: teamsPayload, loaded: true };
  }),
  on(MyOrganizationActions.getManageTeamsListFailure, (state, { error }) => ({ ...state, error })),

  // reducer for deleteManageData
  on(MyOrganizationActions.deleteManageData, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(MyOrganizationActions.deleteManageDataSuccess, (state, { deleteManageDataMessage }) => {
    return { ...state, deleteCustomDataMessage: deleteManageDataMessage, loaded: true };
  }),
  on(MyOrganizationActions.deleteManageDataFailure, (state, { error }) => ({ ...state, error })),
  on(MyOrganizationActions.openInvoiceModalSuccess, (state, { invoiceData }) => {
    return { ...state, invoiceData: invoiceData };
  }),

  on(MyOrganizationActions.getProjectWorkflowListSuccess, (state, { projectWorkflowList }) => ({
    ...state,
    projectWorkflowList: projectWorkflowList,
  })),
  on(
    MyOrganizationActions.getManageScenarioWorkflowSuccess,
    (state, { manageScenarioWorkflowList }) => ({
      ...state,
      manageScenarioWorkflow: manageScenarioWorkflowList,
    })
  ),

  on(MyOrganizationActions.getProductGroupsListSuccess, (state, { ProductGroupsList }) => ({
    ...state,
    productGroupsList: ProductGroupsList,
  })),

  on(MyOrganizationActions.getSupplierListSuccess, (state, { SupplierList }) => ({
    ...state,
    suppliersList: SupplierList,
  })),

  on(MyOrganizationActions.getLitterImpactDataSuccess, (state, { litterImpactData }) => {
    return { ...state, litterImpactData: litterImpactData.eolListItem };
  }),

  on(MyOrganizationActions.getYearEndsOnDataSuccess, (state, { yearEndsOnData }) => {
    return {
      ...state,
      yearEndsOnData: yearEndsOnData[0].organizationYearEndDate,
      yearEndsReplica: yearEndsOnData,
    };
  }),
  on(MyOrganizationActions.getCustomCategoryListSuccess, (state, { customCategoryList }) => {
    return { ...state, customCategoryData: customCategoryList };
  }),
  on(MyOrganizationActions.getCustomSpeedosDataSuccess, (state, { customSpeedosData }) => {
    return { ...state, customSpeedosData: customSpeedosData };
  }),
  on(MyOrganizationActions.getPackagingMetricsDataSuccess, (state, { packagingMetricsData }) => {
    return { ...state, packagingMetricsData: packagingMetricsData };
  }),
  on(MyOrganizationActions.getProjectSavingLogsListSuccess, (state, { ProjectSavingLogsList }) => {
    return { ...state, projectSavingLogsData: ProjectSavingLogsList };
  }),
  on(MyOrganizationActions.getSSOGroupListSuccess, (state, { ssoGroupList }) => {
    return { ...state, ssoGroupList: ssoGroupList };
  }),
  on(MyOrganizationActions.getManageUserDetailsListSuccess, (state, { manageUserDetailsList }) => {
    return { ...state, manageUserDetailsData: manageUserDetailsList };
  }),
  on(
    MyOrganizationActions.getManageUserTeamMemberShipGridDataSuccess,
    (state, { manageTeamMembershipList }) => {
      return { ...state, manageTeamMembershipData: manageTeamMembershipList };
    }
  ),
  on(
    MyOrganizationActions.getAdminOrganizationMemberShipGridDataSuccess,
    (state, { adminOrganizationMembershipList }) => {
      return { ...state, adminOrganizationMembershipData: adminOrganizationMembershipList };
    }
  ),
  on(MyOrganizationActions.getTeamGridDataSuccess, (state, { manageTeamList }) => {
    return { ...state, manageTeamData: manageTeamList };
  }),
  // Reducer For openAddEditManageUserModal
  on(
    MyOrganizationActions.openAddEditMyOrganizationManageUserModalSuccess,
    (state, { myOrganizationManagageUserData }) => {
      return { ...state, myOrganizationManagageUserData: myOrganizationManagageUserData };
    }
  ),
  // Reducer For downloadInvoice
  on(MyOrganizationActions.invoiceDownloadDataSuccess, (state, { invoiceDownloadData }) => {
    return { ...state, invoiceDownloadData: invoiceDownloadData };
  }),

  on(MyOrganizationActions.getOrgWeightingsList, (state, { data }) => {
    const indexedData = data.map((x: any, i: any) => {
      return { ...x, index: i };
    });
    return { ...state, orgWeightingData: indexedData };
  }),

  on(
    MyOrganizationActions.updateOrgWeightingsList,
    (state, { process, action, processIndex, colId }) => {
      const editedProcess = { ...process };
      const payload = {
        defaultValue: editedProcess.defaultValue,
        indicatorId: editedProcess.indicatorId,
      };
      localStorage.setItem('editedWeighting', JSON.stringify(payload));
      return produce(state, (draftState: any) => {
        const indx = draftState.orgWeightingData.findIndex(
          (data: any) => data.index === editedProcess.index
        );
        draftState.orgWeightingData[indx]['defaultValue'] = editedProcess['defaultValue'];
      });
    }
  ),

  on(
    MyOrganizationActions.inlineDataEditForOrganizationPrefSuccess,
    (state, { prefrencesData, module, field }) => {
      return produce(state, draftState => {
        const indx = draftState.weightingList.findIndex(
          (data: any) => data.indicatorId === prefrencesData
        );
        draftState.weightingList[indx][field] = module;
      });
    }
  ),

  // FOR ADD SKU year grid
  on(MyOrganizationActions.addSKUYearProcess, (state, { process, action, processIndex }) => {
    process = cloneDeep(process);
    process['index'] = state.skuYearProcess.length + 1;
    process['enableEditing'] = true;
    return {
      ...state,
      skuYearProcess: [process, ...state.skuYearProcess],
      skuYearProcessError: 'Please fill all the mandatory information to proceed further.',
    };
  }),

  on(
    MyOrganizationActions.updateSKUYearProcess,
    (state, { process, action, processIndex, nodeId, colId }) => {
      const editedProcess = cloneDeep(process);
      return produce(state, (draftState: any) => {
        const indx = draftState.skuYearProcess.findIndex(
          (data: any) => data.index === editedProcess.index
        );
        draftState.skuYearProcess[indx][colId] = editedProcess[colId];
        draftState.skuYearProcess[indx]['enableEditing'] = editedProcess['enableEditing'];
        let processRequired = draftState.skuYearProcess.filter(
          (data: any) => data?.year == 'Select a year'
        ).length
          ? true
          : false;
        const unitsRequired = draftState.skuYearProcess.filter(
          (data: any) => !data?.numberOfUnitsProduced
        ).length
          ? true
          : false;
        if (colId == 'numberOfUnitsProduced') {
          draftState.skuYearProcess[indx]['numberOfUnitsProduced'] =
            !editedProcess?.numberOfUnitsProduced ? 0 : editedProcess?.numberOfUnitsProduced;
        } else if (colId == 'year' && !editedProcess?.year) {
          draftState.skuYearProcess[indx]['year'] = 'Select a year';
          processRequired = true;
        } else if (colId == 'productGroupName') {
          draftState.skuYearProcess[indx]['productGroupId'] = editedProcess['productGroupId'];
        }

        draftState.skuYearProcessError = unitsRequired
          ? 'Please enter number of units'
          : processRequired
          ? 'Please select a year'
          : '';
      });
    }
  ),

  on(MyOrganizationActions.deleteSKUYearProcess, (state, { process, action, processIndex }) => {
    const deletedProcess = cloneDeep(process);
    let filteredData = state.skuYearProcess.filter(
      (node: any) => node.index !== deletedProcess.index
    );
    filteredData = indexRemapping(filteredData);
    const error = validationMessage(filteredData, 'year', 'numberOfUnitsProduced');
    return {
      ...state,
      skuYearProcess: [...filteredData],
      skuYearProcessError: error,
    };
  }),

  on(MyOrganizationActions.clearSKUYearProcess, state => {
    return { ...state, skuYearProcess: [], skuYearProcessError: '' };
  }),

  on(MyOrganizationActions.patchDataOnOpenSKUModal, (state, { data }) => {
    const indexedData = data.map((x: any, i: any) => {
      return { ...x, index: i };
    });
    return { ...state, skuYearProcess: indexedData };
  }),

  on(MyOrganizationActions.checkUpdateActionSKU, (state, { check }) => ({
    ...state,
    checkUpdateActionSKU: check,
  })),

  on(MyOrganizationActions.updateLitterImpactData, (state, { eolListItem }) => {
    eolListItem = { ...eolListItem };
    return produce(state, (draftState: any) => {
      const indx = draftState.litterImpactData.findIndex(
        (data: any) => data.packagingLevelName === eolListItem.packagingLevelName
      );
      draftState.litterImpactData[indx] = eolListItem;
    });
  }),

  on(MyOrganizationActions.resetWeightingsList, (state, { process, action }) => {
    const editedProcess = { ...process };
    return produce(state, (draftState: any) => {
      const indx = draftState.orgWeightingData.findIndex(
        (data: any) => data.index === editedProcess.index
      );
      draftState.orgWeightingData[indx]['defaultValue'] = null;
    });
  })
);

export function myOrganizationReducer(state: MyOrganizationState | undefined, action: Action) {
  return reducer(state, action);
}
